import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { digitsOnly } from '../lib/validation';
import { formatRSD, formatCur } from '../lib/helpers';
import Icon from './Icon';

// ============================================================
// UVOZ FAKTURA IZ EXCELA (šablon FinArt_uvoz_faktura.xlsx)
// Jedan red = jedna stavka; isti broj fakture = ista faktura.
// ============================================================

const H = {
  number: 'Broj fakture *', kind: 'Vrsta', issue: 'Datum fakture *', service: 'Datum prometa', due: 'Datum valute',
  place: 'Mesto prometa', cName: 'Klijent - naziv *', cPib: 'Klijent - PIB', cMb: 'Klijent - matični broj',
  cAddress: 'Klijent - adresa', cPostal: 'Klijent - poštanski broj', cCity: 'Klijent - grad', cForeign: 'Strani klijent',
  itemName: 'Opis stavke *', unit: 'Jedinica mere', qty: 'Količina', price: 'Cena *', discount: 'Popust %',
  currency: 'Valuta', rate: 'Kurs NBS', status: 'Status', paid: 'Uplaćeno', note: 'Poruka',
};

const norm = (s) => String(s ?? '').toLowerCase().replace(/đ/g, 'dj').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
const str = (v) => (v === null || v === undefined ? '' : String(v).trim());
const pad = (n) => String(n).padStart(2, '0');

function num(v) {
  if (typeof v === 'number') return v;
  let s = str(v).replace(/\s/g, '');
  if (!s) return NaN;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.'); // 12.000,50 → 12000.50
  return parseFloat(s);
}

function toIsoDate(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const d = new Date(v.getTime() + 12 * 3600 * 1000); // zaštita od pomeranja vremenske zone
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  if (typeof v === 'number') {
    const p = XLSX.SSF.parse_date_code(v);
    return p ? `${p.y}-${pad(p.m)}-${pad(p.d)}` : '';
  }
  const s = str(v);
  let m = s.match(/^(\d{1,2})\s*[./-]\s*(\d{1,2})\s*[./-]\s*(\d{4})\.?$/);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  return '';
}

function kindOf(v) {
  const k = norm(v);
  if (k.startsWith('pro')) return 'profaktura';
  if (k.startsWith('fisk')) return 'fiskalni';
  return 'faktura';
}

function statusOf(v, kind) {
  const s = norm(v);
  if (s.startsWith('naplac')) return 'naplaceno';
  if (s.startsWith('delim')) return 'delimicno';
  if (s.startsWith('storn')) return 'stornirano';
  if (s.includes('priprem')) return 'u_pripremi';
  if (s.startsWith('poslat')) return 'poslato';
  if (kind === 'fiskalni') return 'naplaceno';
  if (kind === 'profaktura') return 'u_pripremi';
  return 'poslato';
}

const KIND_LABEL = { faktura: 'Faktura', profaktura: 'Profaktura', fiskalni: 'Fiskalni račun' };

// Iz redova tabele napravi listu faktura sa stavkama i spisak grešaka
function parseRows(rows) {
  const groups = new Map();
  const errors = [];
  rows.forEach((r, idx) => {
    const line = idx + 2; // red u Excelu (1 = naslovi)
    const number = str(r[H.number]);
    const anyValue = Object.values(r).some((v) => str(v) !== '');
    if (!anyValue) return;
    if (/^primer/i.test(number)) return; // red sa primerom
    if (!number) { errors.push(`Red ${line}: fali broj fakture.`); return; }
    const kind = kindOf(r[H.kind]);
    const key = `${kind}|${number}`;
    if (!groups.has(key)) {
      const issue = toIsoDate(r[H.issue]);
      const currency = (str(r[H.currency]) || 'RSD').toUpperCase();
      groups.set(key, {
        line, number, kind, issue,
        service: toIsoDate(r[H.service]) || issue,
        due: toIsoDate(r[H.due]) || null,
        place: str(r[H.place]),
        client: {
          name: str(r[H.cName]), pib: digitsOnly(r[H.cPib]), mb: digitsOnly(r[H.cMb]),
          address: str(r[H.cAddress]), postal_code: str(r[H.cPostal]), city: str(r[H.cCity]),
          is_foreign: norm(r[H.cForeign]) === 'da',
        },
        currency, rate: num(r[H.rate]),
        status: statusOf(r[H.status], kind), paid: num(r[H.paid]),
        note: str(r[H.note]),
        items: [],
      });
    }
    const g = groups.get(key);
    const name = str(r[H.itemName]);
    const price = num(r[H.price]);
    const qtyRaw = num(r[H.qty]);
    const disc = num(r[H.discount]);
    if (!name) errors.push(`Red ${line} (${number}): fali opis stavke.`);
    if (!(price > 0)) errors.push(`Red ${line} (${number}): fali cena ili nije veća od nule.`);
    g.items.push({
      name, unit: str(r[H.unit]),
      quantity: Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1,
      price: Number.isFinite(price) ? price : 0,
      discount: Number.isFinite(disc) ? Math.min(Math.max(disc, 0), 100) : 0,
    });
  });

  const invoices = [...groups.values()].map((g) => {
    const total = Math.round(g.items.reduce((s, it) => s + it.quantity * it.price * (1 - it.discount / 100), 0) * 100) / 100;
    const foreign = g.currency !== 'RSD' && g.kind !== 'fiskalni';
    if (!g.issue) errors.push(`Faktura ${g.number}: datum fakture nije prepoznat (upišite npr. 13.10.2026).`);
    if (!g.client.name && g.kind !== 'fiskalni') errors.push(`Faktura ${g.number}: fali naziv klijenta.`);
    if (foreign && !(g.rate > 0)) errors.push(`Faktura ${g.number}: za valutu ${g.currency} fali kurs NBS.`);
    return { ...g, total, foreign };
  });
  return { invoices, errors };
}

export default function ImportInvoices({ onClose, onDone }) {
  const { activeAgencyId, activeAgency, refreshYears } = useAgency();
  const fileRef = useRef(null);
  const [step, setStep] = useState('pick'); // pick | preview | importing | done
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const readFile = async (file) => {
    setError('');
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const ws = wb.Sheets.Fakture || wb.Sheets[wb.SheetNames.find((n) => n !== 'Uputstvo') || wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
      if (!rows.length || !(H.number in rows[0])) {
        setError('Ovo ne liči na FinArt šablon za fakture (ne vidim kolonu "Broj fakture *"). Koristite šablon iz aplikacije.');
        return;
      }
      const p = parseRows(rows);
      if (!p.invoices.length) { setError('U tabeli nema faktura za uvoz.'); return; }
      setFileName(file.name);
      setParsed(p);
      setStep('preview');
    } catch (err) {
      setError(`Fajl nije mogao da se pročita: ${err.message}`);
    }
  };

  const runImport = async () => {
    setStep('importing');
    const invs = parsed.invoices;
    setProgress({ done: 0, total: invs.length });
    const res = { created: 0, skipped: [], failed: [], newClients: 0 };

    // Postojeći klijenti i brojevi faktura u ovom nalogu
    const [{ data: customers }, { data: existing }] = await Promise.all([
      supabase.from('customers').select('*').eq('agency_id', activeAgencyId),
      supabase.from('invoices').select('invoice_number, document_kind, issue_date').eq('agency_id', activeAgencyId),
    ]);
    const byPib = new Map();
    const byName = new Map();
    (customers || []).forEach((c) => {
      if (c.pib) byPib.set(digitsOnly(c.pib) || c.pib, c);
      byName.set(norm(c.name), c);
    });
    const taken = new Set((existing || []).map((e) => `${e.document_kind}|${String(e.invoice_number || '').trim()}|${String(e.issue_date).slice(0, 4)}`));

    for (let i = 0; i < invs.length; i++) {
      const g = invs[i];
      try {
        const dupKey = `${g.kind}|${g.number}|${g.issue.slice(0, 4)}`;
        if (taken.has(dupKey)) { res.skipped.push(`${KIND_LABEL[g.kind]} ${g.number} (već postoji)`); continue; }

        // Klijent: postojeći po PIB-u ili nazivu, inače novi
        let customer = null;
        if (g.client.name) {
          customer = (g.client.pib && byPib.get(g.client.pib)) || byName.get(norm(g.client.name)) || null;
          if (!customer) {
            const { data: c, error: cErr } = await supabase.from('customers').insert({
              agency_id: activeAgencyId, ...g.client, country: g.client.is_foreign ? '' : 'Srbija', email: '', phone: '',
            }).select().single();
            if (cErr) throw new Error(`klijent "${g.client.name}": ${cErr.message}`);
            customer = c;
            res.newClients += 1;
            if (c.pib) byPib.set(c.pib, c);
            byName.set(norm(c.name), c);
          }
        }

        const payload = {
          agency_id: activeAgencyId,
          customer_id: customer?.id || null,
          client: customer?.name || g.client.name || '',
          document_kind: g.kind,
          type: g.foreign ? 'inostrana' : 'domaca',
          invoice_number: g.number,
          items: g.items,
          description: g.items.map((it) => it.name).join('; '),
          amount: g.total,
          currency: g.foreign ? g.currency : 'RSD',
          exchange_rate: g.foreign ? g.rate : 1,
          issue_date: g.issue,
          service_date: g.service,
          due_date: g.kind === 'fiskalni' ? null : g.due,
          place_of_issue: g.place,
          note: g.note || null,
          status: g.status,
          paid_amount: g.status === 'naplaceno' ? g.total : g.status === 'delimicno' ? Math.min(g.paid || 0, g.total) : 0,
          paid_at: g.status === 'naplaceno' || g.status === 'delimicno' ? g.issue : null,
        };
        const { error: iErr } = await supabase.from('invoices').insert(payload);
        if (iErr) throw new Error(iErr.message);
        taken.add(dupKey);
        res.created += 1;
      } catch (err) {
        res.failed.push(`${KIND_LABEL[g.kind]} ${g.number}: ${err.message}`);
      } finally {
        setProgress({ done: i + 1, total: invs.length });
      }
    }
    if (refreshYears) refreshYears();
    setResult(res);
    setStep('done');
  };

  const money = (inv) => (inv.foreign ? formatCur(inv.total, inv.currency) : formatRSD(inv.total));
  const totalRsd = parsed ? parsed.invoices.reduce((s, inv) => s + (inv.foreign ? inv.total * (inv.rate || 0) : inv.total), 0) : 0;

  return (
    <div style={styles.overlay} onClick={() => step !== 'importing' && onClose()}>
      <div style={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div style={styles.head}>
          <div>
            <div style={styles.title}>Uvoz faktura iz Excela</div>
            <div style={styles.sub}>Nalog: <strong>{activeAgency?.name}</strong></div>
          </div>
          {step !== 'importing' && (
            <button onClick={onClose} style={styles.closeBtn} aria-label="Zatvori"><Icon name="x" size={16} /></button>
          )}
        </div>

        {step === 'pick' && (
          <>
            <div role="button" tabIndex={0} style={styles.drop}
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); readFile(e.dataTransfer.files?.[0]); }}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
              <Icon name="fileText" size={26} color="var(--lavender-700)" />
              <strong>Izaberi popunjen Excel fajl</strong>
              <span style={styles.muted}>ili ga prevuci ovde</span>
            </div>
            <a href="/FinArt_uvoz_faktura.xlsx" download style={styles.templateLink}>
              <Icon name="download" size={15} /> Preuzmi prazan šablon za fakture
            </a>
          </>
        )}

        {step === 'preview' && parsed && (
          <>
            <div style={styles.summary}>
              <div><strong>{parsed.invoices.length}</strong> dokumenata iz fajla <em>{fileName}</em></div>
              <div style={styles.muted}>Ukupno približno {formatRSD(totalRsd)}</div>
            </div>
            {parsed.errors.length > 0 && (
              <div style={styles.errorBox}>
                <strong>Ispravite u Excelu pre uvoza ({parsed.errors.length}):</strong>
                <ul style={styles.ul}>{parsed.errors.slice(0, 12).map((e) => <li key={e}>{e}</li>)}</ul>
                {parsed.errors.length > 12 && <div>...i još {parsed.errors.length - 12}.</div>}
              </div>
            )}
            <div style={styles.list}>
              {parsed.invoices.map((inv) => (
                <div key={`${inv.kind}|${inv.number}`} style={styles.row}>
                  <span style={styles.no}>{inv.number}</span>
                  <span style={styles.rowMid}>
                    <span style={styles.rowClient}>{inv.client.name || '—'}</span>
                    <span style={styles.muted}>{KIND_LABEL[inv.kind]} · {inv.issue ? inv.issue.split('-').reverse().join('.') : '?'} · {inv.items.length} {inv.items.length === 1 ? 'stavka' : 'stavki'}</span>
                  </span>
                  <span style={styles.amount}>{money(inv)}</span>
                </div>
              ))}
            </div>
            <div style={styles.note}>Fakture čiji broj već postoji u ovom nalogu biće preskočene. Novi klijenti se automatski dodaju na listu Klijenti.</div>
            <div style={styles.actions}>
              <button onClick={() => { setStep('pick'); setParsed(null); }} style={styles.cancelBtn}>Izaberi drugi fajl</button>
              <button onClick={runImport} disabled={parsed.errors.length > 0} style={{ ...styles.primaryBtn, opacity: parsed.errors.length ? 0.5 : 1 }}>
                <Icon name="check" size={16} strokeWidth={2.2} /> Uvezi {parsed.invoices.length}
              </button>
            </div>
          </>
        )}

        {step === 'importing' && (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Uvozim... {progress.done} / {progress.total}</div>
            <div style={styles.track}><div style={{ ...styles.fill, width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} /></div>
            <div style={{ ...styles.muted, marginTop: 10 }}>Ne zatvarajte prozor dok se uvoz ne završi.</div>
          </div>
        )}

        {step === 'done' && result && (
          <>
            <div style={styles.okBox}>
              Uvezeno: <strong>{result.created}</strong> dokumenata{result.newClients ? `, dodato novih klijenata: ${result.newClients}` : ''}.
            </div>
            {result.skipped.length > 0 && (
              <div style={styles.warnBox}><strong>Preskočeno ({result.skipped.length}):</strong>
                <ul style={styles.ul}>{result.skipped.slice(0, 10).map((s) => <li key={s}>{s}</li>)}</ul></div>
            )}
            {result.failed.length > 0 && (
              <div style={styles.errorBox}><strong>Nije uspelo ({result.failed.length}):</strong>
                <ul style={styles.ul}>{result.failed.slice(0, 10).map((s) => <li key={s}>{s}</li>)}</ul></div>
            )}
            <div style={styles.actions}>
              <button onClick={() => { onDone && onDone(); onClose(); }} style={styles.primaryBtn}>Gotovo</button>
            </div>
          </>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--surface)', borderRadius: 10, padding: 22, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' },
  sub: { fontSize: 13, color: 'var(--ink-500)', marginTop: 2 },
  closeBtn: { width: 36, height: 36, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)' },
  drop: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 150, border: '1.5px dashed var(--lavender-500)', background: 'var(--lavender-100)', borderRadius: 10, padding: 16, textAlign: 'center', cursor: 'pointer', color: 'var(--ink-900)' },
  muted: { fontSize: 12, color: 'var(--ink-500)' },
  templateLink: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 13, fontWeight: 700, color: 'var(--teal-700)', textDecoration: 'none' },
  summary: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', marginBottom: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--line)', borderRadius: 10, padding: '8px 12px' },
  no: { fontSize: 13, fontWeight: 800, color: 'var(--teal-800)', background: 'var(--teal-100)', borderRadius: 7, padding: '2px 8px', whiteSpace: 'nowrap' },
  rowMid: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  rowClient: { fontSize: 14, fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  amount: { fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--ink-900)' },
  note: { fontSize: 12, color: 'var(--ink-500)', marginBottom: 14 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 4 },
  cancelBtn: { minHeight: 42, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '0 16px', fontWeight: 600, fontSize: 14, color: 'var(--ink-700)' },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 42, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10, padding: '0 18px', fontWeight: 700, fontSize: 14 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  warnBox: { background: 'var(--gold-100)', color: 'var(--gold-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  okBox: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '12px 14px', fontSize: 14, marginBottom: 12 },
  ul: { margin: '6px 0 0', paddingLeft: 18 },
  track: { height: 10, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 99, overflow: 'hidden' },
  fill: { height: '100%', background: 'var(--lavender-700)', transition: 'width 0.2s' },
};
